import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { Spinner } from '../../components/Spinner'
import { Sentinel } from '../../library/Sentinel'
import { Dialog } from '../../of/home/Dialog'
import type { Playable } from '../data'
import { albumsOf, albumTitle, loadVaultPage, type MediaFilter, type PickedMedia, type VaultAlbum } from '../vault'
import { useFanslyApi } from '../deps'
import { uploadToFansly } from '../upload'
import { toFanslyError } from '../useFanslyQuery'
import { FsThumb } from './FsMedia'

interface PickerProps {
  title?: string
  confirmLabel?: string
  /** Most items that may be picked (e.g. 1 for a profile picture). */
  max?: number
  /** Only these kinds can be picked. */
  kinds?: Array<Playable['kind']>
  onPick: (items: PickedMedia[]) => void
  onClose: () => void
}

/**
 * Pick vault files: albums as chips, the album's media in a grid (paged),
 * type filter, upload a new file into the vault. Shared by the chat, post
 * and mass message composers.
 */
export function VaultPicker({ title, confirmLabel, max = 50, kinds, onPick, onClose }: PickerProps) {
  const { t } = useTranslation('fansly')
  const api = useFanslyApi()
  const [albums, setAlbums] = useState<VaultAlbum[] | null>(null)
  const [albumId, setAlbumId] = useState('')
  const [filter, setFilter] = useState<MediaFilter>('')
  const [items, setItems] = useState<PickedMedia[]>([])
  const [next, setNext] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [picked, setPicked] = useState<PickedMedia[]>([])
  const [uploading, setUploading] = useState<number | null>(null)
  const seq = useRef(0)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let live = true
    api
      .get('/crm/vault/albums')
      .then((p) => {
        if (!live) return
        const list = albumsOf(p)
        setAlbums(list)
        setAlbumId((cur) => cur || list[0]?.id || '')
      })
      .catch((e: unknown) => live && setError(toFanslyError(e).message))
    return () => {
      live = false
    }
  }, [api])

  const load = useCallback(
    async (reset: boolean) => {
      if (!albumId) return
      const mine = reset ? ++seq.current : seq.current
      setLoading(true)
      setError('')
      try {
        const page = await loadVaultPage(api, albumId, { mediaType: filter, before: reset ? '' : next })
        if (seq.current !== mine) return
        setItems((prev) => {
          const base = reset ? [] : prev
          const seen = new Set(base.map((i) => i.mediaId))
          return [...base, ...page.items.filter((i) => !seen.has(i.mediaId))]
        })
        setNext(page.items.length ? page.next : '')
      } catch (e) {
        if (seq.current === mine) setError(toFanslyError(e).message)
      } finally {
        if (seq.current === mine) setLoading(false)
      }
    },
    [api, albumId, filter, next],
  )

  // A new album or filter starts over.
  useEffect(() => {
    setItems([])
    setNext('')
    void load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumId, filter])

  const toggle = (m: PickedMedia) => {
    setPicked((cur) => {
      if (cur.some((p) => p.mediaId === m.mediaId)) return cur.filter((p) => p.mediaId !== m.mediaId)
      if (cur.length >= max) return max === 1 ? [m] : cur
      return [...cur, m]
    })
  }

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    setError('')
    try {
      for (let i = 0; i < files.length; i += 1) {
        setUploading(0)
        await uploadToFansly(api, files[i]!, { onProgress: (p) => setUploading(p.fraction) })
      }
      // New files land in the All album.
      const all = albums?.find((a) => a.type === 38000)
      if (all && all.id !== albumId) setAlbumId(all.id)
      else void load(true)
    } catch (e) {
      setError(toFanslyError(e).message)
    } finally {
      setUploading(null)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const allowed = useMemo(() => (kinds ? new Set(kinds) : null), [kinds])
  const shown = allowed ? items.filter((i) => allowed.has(i.media.kind)) : items

  return (
    <Dialog
      wide
      title={title ?? t('vaultPicker.title')}
      onClose={onClose}
      head={
        <button type="button" className="lib-btn" disabled={uploading !== null} onClick={() => fileRef.current?.click()}>
          {uploading !== null ? <Spinner size={14} label={t('vaultPicker.uploading')} /> : <FeatherIcon name="upload" size={14} />}
          {uploading !== null ? `${Math.round(uploading * 100)}%` : t('vaultPicker.upload')}
        </button>
      }
      actions={
        <>
          <span className="fs-picker-count">{t('vaultPicker.selected', { count: picked.length })}</span>
          <button type="button" className="lib-btn" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--primary" disabled={!picked.length} onClick={() => onPick(picked)}>
            {confirmLabel ?? t('vaultPicker.add')}
          </button>
        </>
      }
    >
      <input ref={fileRef} type="file" hidden multiple accept={kinds?.length === 1 && kinds[0] === 'audio' ? 'audio/*' : 'image/*,video/*,audio/*'} onChange={(e) => void upload(e.target.files)} />
      <div className="fs-picker">
        <div className="fs-picker-albums" role="tablist" aria-label={t('vaultPicker.albums')}>
          {albums === null ? (
            <Spinner size={16} label={t('loading')} />
          ) : (
            albums.map((a) => (
              <button key={a.id} type="button" role="tab" aria-selected={a.id === albumId} className={`fs-tab ${a.id === albumId ? 'is-on' : ''}`} onClick={() => setAlbumId(a.id)}>
                {albumTitle(a, t)}
                <span className="fs-picker-n">{a.count}</span>
              </button>
            ))
          )}
        </div>
        <div className="fs-picker-filters">
          {(['', '1', '2', '3'] as MediaFilter[]).map((f) => (
            <button key={f || 'all'} type="button" className={`lib-chip ${filter === f ? 'is-on' : ''}`} onClick={() => setFilter(f)}>
              {t(`vaultPicker.filter.${f || 'all'}`)}
            </button>
          ))}
        </div>
        <div className="fs-picker-grid">
          {error && <div className="ofp-error">{error}</div>}
          <div className="fs-grid">
            {shown.map((m) => (
              <FsThumb key={m.mediaId} media={m.media} selected={picked.some((p) => p.mediaId === m.mediaId)} onOpen={() => toggle(m)} />
            ))}
          </div>
          {loading && <Spinner size={22} label={t('loading')} />}
          {!loading && !error && !shown.length && <div className="fs-empty">{t('vaultPicker.empty')}</div>}
          {!loading && next && <Sentinel onVisible={() => void load(false)} token={items.length} />}
        </div>
      </div>
    </Dialog>
  )
}
